import { InteractiveElement } from '@/client/courses/types/courses.type';
import { ContentInlineFormattingToolbar } from '@/components/courses/ContentInlineFormattingToolbar';
import { setupColorPickerForBubble } from '@/ui/rich-text';
import { FlexContainer } from '@/ui/styled-ui';
import {
  SMSArrowIcon,
  SMSBatteryIcon,
  SMSMessageTailIcon,
  SMSProfileIcon,
  SMSSignalBarsIcon,
  SMSWiFiIcon,
} from '@/utils/helpers/icons.helper';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// TODO: move to '@/ui/rich-text' — react-quill is being confined there
// so the editor can be swapped in one place.
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.core.css';
import styled, { css } from 'styled-components';

const SMSEditorContainer = styled.div<{ readOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 300px;
  flex-shrink: 0;
  background: var(--white-main);
  border-radius: var(--semi-medium-border-radius);
  border: 1px solid var(--gray-dark);
  width: 300px;
  min-height: 370px;
  position: relative;

  ${({ readOnly }) =>
    readOnly &&
    css`
      .quill {
        &:hover {
          border-color: transparent !important;
        }
      }
    `}
`;

const SMSHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  padding-top: 6px;
  color: var(--white-main);
  min-height: 80px;
  border-bottom: 1px solid var(--gray-dark);
  position: relative;
  border-radius: var(--semi-medium-border-radius)
    var(--semi-medium-border-radius) 0 0;
  background: var(--purple-dark);
  padding-bottom: 0;
  direction: ltr;

  svg {
    margin: 0;
    padding: 0;
  }
`;

const StyledDate = styled.div`
  font-size: 8px;
  color: #b3a9b2;
  font-weight: 600;
  flex-shrink: 0;
`;

const ProfileIcon = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white-main);
  font-size: 20px;
`;

const SenderNameInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  font-size: var(--xsmall-font-size);
  line-height: 1.4;
  text-align: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: var(--white-main);
  outline: none;
  margin-bottom: 10px;
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    border-color: var(--gray-dark);
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

const SMSBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--small-padding);
  background: var(--white-main);
  height: 100%;
  border-bottom-right-radius: var(--semi-medium-border-radius);
  border-bottom-left-radius: var(--semi-medium-border-radius);
  position: relative;
`;

const MessageBubble = styled.div`
  display: inline-block;
  max-width: 80%;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const StyledSVG = styled.div<{ rtl?: boolean }>`
  position: absolute;
  bottom: 10px;
  left: ${({ rtl }) => (rtl ? 'auto' : '5px')};
  right: ${({ rtl }) => (rtl ? '5px' : 'auto')};
  transform: ${({ rtl }) => (rtl ? 'scaleX(-1)' : 'none')};
  z-index: 0;
`;

const StyledMessageQuill = styled(ReactQuill)`
  z-index: 1;
  border-radius: 9px !important;

  .ql-container {
    border: none;
    font-size: var(--small-font-size);
    padding: 0;
    border-radius: 9px !important;
    padding: 10px;
  }

  .ql-editor {
    * {
      font-size: var(--small-font-size) !important;
      line-height: 1.5 !important;
    }

    p {
      color: var(--black-main);
    }
  }
`;

type ContentSMSComponentProps = {
  id?: string;
  interactiveElement: InteractiveElement;
  onUpdate?: (data: { sender?: string; message?: string }) => void;
  readOnly?: boolean;
  rtl?: boolean;
};

type SMSData = {
  sender?: string;
  message?: string;
};

export const ContentSMSComponent: React.FC<ContentSMSComponentProps> = ({
  id,
  interactiveElement,
  onUpdate,
  readOnly = false,
  rtl = false,
}) => {
  const { t } = useTranslation();
  const messageQuillRef = useRef<any>(null);
  const [smsData, setSmsData] = useState<SMSData>({
    sender: interactiveElement.data?.sender,
    message: interactiveElement.data?.message,
  });

  useEffect(() => {
    setSmsData({
      sender: interactiveElement.data?.sender,
      message: interactiveElement.data?.message,
    });
  }, [interactiveElement.data, id]);

  useEffect(() => {
    if (readOnly || !messageQuillRef.current) return;

    const colorButtonId = `custom-color-btn-sms-${id}`;
    const colorInputId = `custom-color-input-sms-${id}`;

    return setupColorPickerForBubble(
      messageQuillRef,
      colorButtonId,
      colorInputId,
    );
  }, [readOnly, messageQuillRef.current, id]);

  const handleMessageChange = (html: string) => {
    const newData = { ...smsData, message: html };
    setSmsData(newData);
    onUpdate?.(newData);
  };

  const handleSenderChange = (value: string) => {
    const newData = { ...smsData, sender: value };
    setSmsData(newData);
    onUpdate?.(newData);
  };

  const toolbarId = `inline-formatting-toolbar-sms-${id}`;
  const colorButtonId = `custom-color-btn-sms-${id}`;
  const colorInputId = `custom-color-input-sms-${id}`;

  return (
    <SMSEditorContainer
      key={id}
      readOnly={readOnly}
      translate="no"
      className="notranslate"
    >
      {!readOnly && (
        <ContentInlineFormattingToolbar
          toolbarId={toolbarId}
          colorButtonId={colorButtonId}
          colorInputId={colorInputId}
        />
      )}
      <SMSHeader>
        <FlexContainer
          justify="space-between"
          style={{ height: '12px', marginBottom: 'auto' }}
        >
          <FlexContainer gap={5} align="center" justify="flex-start">
            <StyledDate>{moment().format('DD.MM')}</StyledDate>
            <FlexContainer justify="flex-start">
              <SMSArrowIcon />
            </FlexContainer>
          </FlexContainer>
          <FlexContainer gap={5} align="center" justify="flex-end">
            <SMSSignalBarsIcon />
            <SMSWiFiIcon />
            <SMSBatteryIcon />
          </FlexContainer>
        </FlexContainer>
        <ProfileIcon>
          <SMSProfileIcon />
        </ProfileIcon>
        <SenderNameInput
          type="text"
          value={smsData.sender || ''}
          onChange={(e) => handleSenderChange(e.target.value)}
          placeholder={readOnly ? '' : t('content.sms.senderName')}
          readOnly={readOnly}
          disabled={readOnly}
        />
      </SMSHeader>
      <SMSBodyContainer>
        <MessageBubble>
          <StyledMessageQuill
            key={`sms-message-${id}`}
            value={smsData.message || ''}
            theme="bubble"
            className="rich-text-editor rich-text-editor-v2"
            readOnly={readOnly}
            modules={{
              toolbar: readOnly
                ? false
                : { container: `#inline-formatting-toolbar-sms-${id}` },
              clipboard: {
                matchVisual: false,
              },
            }}
            onChange={(html) => {
              if (!readOnly) {
                handleMessageChange(html);
              }
            }}
            ref={(el) => {
              if (el && messageQuillRef.current !== el.getEditor()) {
                messageQuillRef.current = el.getEditor();
              }
            }}
          />
        </MessageBubble>
        <StyledSVG rtl={rtl}>
          <SMSMessageTailIcon />
        </StyledSVG>
      </SMSBodyContainer>
    </SMSEditorContainer>
  );
};
